import { useEffect, useMemo, useState } from "react";

const KW = "#FF5722";
const STR = "#A5D6A7";
const ID = "#7DD3FC";
const TXT = "#E4E4E7";
const P = "#A1A1AA";

const SNIPPETS = [
  {
    file: "Dashboard.jsx",
    tokens: [
      { t: "const ", c: KW },
      { t: "[users, setUsers]", c: ID },
      { t: " = ", c: P },
      { t: "useState", c: ID },
      { t: "([]);\n\n", c: P },
      { t: "useEffect", c: ID },
      { t: "(() => {\n  ", c: P },
      { t: "api", c: TXT },
      { t: ".get(", c: P },
      { t: '"/api/users"', c: STR },
      { t: ")\n     .then(", c: P },
      { t: "res", c: ID },
      { t: " => ", c: KW },
      { t: "setUsers(res.data));\n", c: TXT },
      { t: "}, []);", c: P },
    ],
  },
  {
    file: "UsersController.cs",
    tokens: [
      { t: "[HttpGet(", c: P },
      { t: '"api/users"', c: STR },
      { t: ")]\n", c: P },
      { t: "public async ", c: KW },
      { t: "Task<IActionResult>", c: ID },
      { t: " Get()\n{\n  ", c: TXT },
      { t: "var ", c: KW },
      { t: "users = ", c: TXT },
      { t: "await ", c: KW },
      { t: "_db.Users.ToListAsync();\n  ", c: TXT },
      { t: "return ", c: KW },
      { t: "Ok(users);\n", c: TXT },
      { t: "}", c: P },
    ],
  },
  {
    file: "LoginTest.java",
    tokens: [
      { t: "@Test\n", c: ID },
      { t: "public void ", c: KW },
      { t: "loginTest()", c: TXT },
      { t: " {\n  ", c: P },
      { t: "driver.get(baseUrl);\n  ", c: TXT },
      { t: "driver.findElement(By.id(", c: TXT },
      { t: '"email"', c: STR },
      { t: "))\n        .sendKeys(", c: TXT },
      { t: '"ibrahim@d1tech.com"', c: STR },
      { t: ");\n  ", c: TXT },
      { t: "driver.findElement(By.id(", c: TXT },
      { t: '"login"', c: STR },
      { t: ")).click();\n", c: TXT },
      { t: "}", c: P },
    ],
  },
  {
    file: "deploy.yml",
    tokens: [
      { t: "name", c: ID },
      { t: ": ", c: P },
      { t: "Deploy to IIS\n", c: TXT },
      { t: "on", c: ID },
      { t: ":\n  ", c: P },
      { t: "push", c: ID },
      { t: ":\n    ", c: P },
      { t: "branches", c: ID },
      { t: ": [ ", c: P },
      { t: "main", c: STR },
      { t: " ]\n", c: P },
      { t: "jobs", c: ID },
      { t: ":\n  ", c: P },
      { t: "deploy", c: ID },
      { t: ":\n    ", c: P },
      { t: "runs-on", c: ID },
      { t: ": ", c: P },
      { t: "windows-latest\n    ", c: STR },
      { t: "steps", c: ID },
      { t: ":\n      - ", c: P },
      { t: "uses", c: ID },
      { t: ": ", c: P },
      { t: "actions/checkout@v4", c: STR },
    ],
  },
];

export const ExperienceCode = () => {
  const [idx, setIdx] = useState(0);
  const [count, setCount] = useState(0);

  const chars = useMemo(() => {
    const out = [];
    SNIPPETS[idx].tokens.forEach((tok) => {
      for (const ch of tok.t) out.push({ ch, c: tok.c });
    });
    return out;
  }, [idx]);

  useEffect(() => {
    setCount(0);
    const typer = setInterval(() => {
      setCount((n) => (n >= chars.length ? n : n + 1));
    }, 18);
    return () => clearInterval(typer);
  }, [chars]);

  useEffect(() => {
    if (count > 0 && count >= chars.length) {
      const hold = setTimeout(() => setIdx((i) => (i + 1) % SNIPPETS.length), 2800);
      return () => clearTimeout(hold);
    }
  }, [count, chars.length]);

  return (
    <div data-testid="experience-code-card" className="border border-[#1F1F1F] bg-[#0A0A0A] w-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#1F1F1F]">
        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        <span data-testid="experience-code-filename" className="ml-3 font-mono text-[10px] tracking-widest text-zinc-500">
          {SNIPPETS[idx].file}
        </span>
        <span className="ml-auto flex gap-1.5">
          {SNIPPETS.map((_, i) => (
            <span key={i} className={`w-1.5 h-1.5 ${i === idx ? "bg-[#FF5722]" : "bg-[#1F1F1F]"} transition-colors`} />
          ))}
        </span>
      </div>
      <pre className="p-5 font-mono text-xs md:text-[13px] leading-relaxed whitespace-pre-wrap min-h-[240px]">
        {chars.slice(0, count).map((c, i) => (
          <span key={i} style={{ color: c.c }}>{c.ch}</span>
        ))}
        <span className="cursor-blink" style={{ color: KW }}>▍</span>
      </pre>
    </div>
  );
};
